/**
 * PostComposer — modal dialog to create or edit a Story / Article / Ad.
 *
 * Rich body input uses a lightweight contentEditable div (no third-party
 * editor to keep bundle size lean). Toolbar exposes bold, italic, headings,
 * lists, quotes, and link insertion via `document.execCommand`. Media is
 * attached via the existing `<FileUpload>` component (backed by Emergent
 * Object Storage); YouTube URLs are validated and stored as media items.
 *
 * Admins/owners see the "Advertisement" option and CTA fields.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  TextB, TextItalic, TextH, ListBullets, ListNumbers, Quotes, LinkSimple,
  Image as ImageIcon, YoutubeLogo, FilePdf, X, Plus, ArrowClockwise,
  Warning,
} from "@phosphor-icons/react";
import { FileUpload } from "@/components/ui/file-upload";
import { createPost, updatePost, publishPost, getPost } from "@/lib/publishApi";
import { formatApiError } from "@/lib/api";
import { sanitizePostHtml } from "@/lib/publishSanitize";
import { cn } from "@/lib/utils";

const DEFAULT_TOPIC_SUGGESTIONS = [
  "Therapy", "Autism", "ADHD", "Special Education", "Parenting",
  "Speech Therapy", "Occupational Therapy", "Behavioral Therapy",
  "Success Story", "Awareness",
];

function exec(command, value = null) {
  // execCommand is deprecated but still works reliably for basic formatting
  // and avoids pulling in a full rich-text editor library for the MVP.
  document.execCommand(command, false, value);
}

// Toolbar button wrapper — clicking a toolbar button normally steals focus
// away from the contentEditable body BEFORE `execCommand` runs, so lists,
// headings, quote, and link commands no-op. `onMouseDown` firing before
// blur + preventDefault keeps the caret inside the editor.
function stopStealFocus(e) {
  e.preventDefault();
}

export function PostComposer({
  open, onOpenChange, canPostAd = false,
  editing = null,       // used when caller already has the full doc
  editingPostId = null, // if set, composer fetches the full post itself
  topicSuggestions = DEFAULT_TOPIC_SUGGESTIONS, onSaved,
}) {
  // ---- Load-in-composer state (drives loading / error UX) ----
  const [loaded, setLoaded] = useState(editing || null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const loadFullPost = useCallback(async (id) => {
    setLoading(true); setLoadError(null);
    try {
      const p = await getPost(id);
      setLoaded(p);
    } catch (e) {
      setLoadError(formatApiError(e));
      setLoaded(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // On open, decide what to load
  useEffect(() => {
    if (!open) return;
    if (editingPostId) {
      loadFullPost(editingPostId);
    } else if (editing) {
      setLoaded(editing);
    } else {
      setLoaded(null);   // new-post mode
      setLoadError(null);
    }
  }, [open, editingPostId, editing, loadFullPost]);

  // ---- Form state (rebuilds from `loaded`) ----
  const [postType, setPostType] = useState("story");
  const [title, setTitle] = useState("");
  const [topicsInput, setTopicsInput] = useState("");
  const [topics, setTopics] = useState([]);
  const [media, setMedia] = useState([]);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  // Reset form whenever `loaded` transitions or a new open happens
  useEffect(() => {
    if (!open) return;
    setPostType(loaded?.post_type || "story");
    setTitle(loaded?.title || "");
    setTopics(loaded?.topics || []);
    setMedia(loaded?.media || []);
    setCtaLabel(loaded?.cta?.label || "");
    setCtaUrl(loaded?.cta?.url || "");
    setTopicsInput(""); setYoutubeUrl("");
    // Delay setting body until DOM node exists (loading state hides it).
    setTimeout(() => {
      if (bodyRef.current) {
        // Sanitize inbound body before seeding the contentEditable — the
        // caller may be an author editing their own post, but defense in
        // depth: never let unsanitized HTML touch the DOM.
        bodyRef.current.innerHTML = sanitizePostHtml(loaded?.body_html);
      }
    }, 0);
  }, [open, loaded]);

  const bodyHtml = () => sanitizePostHtml(bodyRef.current?.innerHTML || "");

  const addTopic = (raw) => {
    const t = String(raw || "").trim().replace(/^#/, "");
    if (!t) return;
    if (topics.includes(t)) return;
    if (topics.length >= 10) return;
    setTopics([...topics, t]);
  };

  const removeTopic = (t) => setTopics(topics.filter(x => x !== t));

  const addImage = (file_id) => {
    if (!file_id) return;
    setMedia(m => [...m, { kind: "image", url: "", file_id, caption: "" }]);
  };

  const addPdf = (file_id) => {
    if (!file_id) return;
    setMedia(m => [...m, { kind: "pdf", url: "", file_id, caption: "Document" }]);
  };

  const addYouTube = () => {
    const u = youtubeUrl.trim();
    if (!u) return;
    if (!/youtube\.com|youtu\.be/.test(u)) {
      toast.error("Please paste a valid YouTube URL");
      return;
    }
    setMedia(m => [...m, { kind: "youtube", url: u, caption: "" }]);
    setYoutubeUrl("");
  };

  const removeMedia = (idx) => setMedia(m => m.filter((_, i) => i !== idx));

  const submit = async (publishNow) => {
    if (loading) return;
    const html = bodyHtml();
    if (!html.replace(/<[^>]+>/g, "").trim()) {
      toast.error("Please write something in the body");
      return;
    }
    if (postType !== "story" && !title.trim()) {
      toast.error("Title is required for articles and advertisements");
      return;
    }
    setSaving(true);
    try {
      const body = {
        post_type: postType,
        title: title.trim() || null,
        body_html: html,
        media,
        topics,
        publish: publishNow && !loaded,
      };
      if (postType === "advertisement" && (ctaLabel || ctaUrl)) {
        body.cta = { label: ctaLabel.trim() || "Learn more", url: ctaUrl.trim() };
      }
      let saved;
      if (loaded) {
        saved = await updatePost(loaded.id, body);
        // If user clicked "Publish/Update & Republish" on a draft OR delinked
        // post, promote it back to published (preserves original post ID and
        // published_at server-side).
        if (publishNow && (loaded.status === "draft" || loaded.status === "delinked")) {
          saved = await publishPost(loaded.id);
        }
      } else {
        saved = await createPost(body);
      }
      const wasEditPublished = loaded?.status === "published";
      toast.success(
        wasEditPublished ? "Post updated" :
        publishNow ? (loaded?.status === "delinked" ? "Republished" : "Published") :
        "Saved as draft"
      );
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const typeOptions = useMemo(() => {
    const opts = [
      { value: "story",   label: "Story — a personal share" },
      { value: "article", label: "Article — long-form content with a title" },
    ];
    if (canPostAd) opts.push({ value: "advertisement", label: "Advertisement — for services or events" });
    return opts;
  }, [canPostAd]);

  const isEditing = !!loaded || !!editingPostId;
  const dialogTitle = loading
    ? "Loading post…"
    : loadError
      ? "Could not load post"
      : isEditing
        ? "Edit post"
        : "New post";

  // ---- Loading state ----
  if (open && loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="post-composer-loading">
          <DialogHeader><DialogTitle>{dialogTitle}</DialogTitle></DialogHeader>
          <div className="py-8 space-y-3">
            <div className="h-4 bg-muted rounded animate-pulse w-1/3" />
            <div className="h-4 bg-muted rounded animate-pulse w-2/3" />
            <div className="h-32 bg-muted rounded animate-pulse" />
            <p className="text-xs text-muted-foreground">
              Fetching your saved content — one moment…
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ---- Error state ----
  if (open && loadError) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md" data-testid="post-composer-error">
          <DialogHeader><DialogTitle>{dialogTitle}</DialogTitle></DialogHeader>
          <div className="py-4 space-y-3 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center">
              <Warning size={24} weight="duotone" className="text-rose-600" />
            </div>
            <p className="text-sm text-foreground">{loadError}</p>
            <p className="text-xs text-muted-foreground">
              Your existing content was not overwritten. Try again or close this dialog.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button onClick={() => editingPostId && loadFullPost(editingPostId)}
                    data-testid="post-composer-retry">
              <ArrowClockwise size={14} className="mr-1.5" /> Retry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="post-composer-dialog"
      >
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Post type */}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Post type</Label>
            <Select value={postType} onValueChange={setPostType} disabled={isEditing}>
              <SelectTrigger data-testid="post-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {typeOptions.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          {postType !== "story" && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Title</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)}
                     placeholder="Give it a headline"
                     data-testid="post-title-input" />
            </div>
          )}

          {/* Body — rich text */}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Body</Label>
            <div className="border border-input rounded-md">
              <div className="flex flex-wrap items-center gap-0.5 border-b border-input px-2 py-1 bg-muted/40">
                <Button type="button" variant="ghost" size="icon" title="Bold"
                        onMouseDown={stopStealFocus}
                        onClick={() => exec("bold")}><TextB size={16} weight="bold" /></Button>
                <Button type="button" variant="ghost" size="icon" title="Italic"
                        onMouseDown={stopStealFocus}
                        onClick={() => exec("italic")}><TextItalic size={16} /></Button>
                <Button type="button" variant="ghost" size="icon" title="Heading"
                        onMouseDown={stopStealFocus}
                        onClick={() => exec("formatBlock", "<h2>")}><TextH size={16} weight="bold" /></Button>
                <Button type="button" variant="ghost" size="icon" title="Bulleted list"
                        onMouseDown={stopStealFocus}
                        onClick={() => exec("insertUnorderedList")}><ListBullets size={16} /></Button>
                <Button type="button" variant="ghost" size="icon" title="Numbered list"
                        onMouseDown={stopStealFocus}
                        onClick={() => exec("insertOrderedList")}><ListNumbers size={16} /></Button>
                <Button type="button" variant="ghost" size="icon" title="Quote"
                        onMouseDown={stopStealFocus}
                        onClick={() => exec("formatBlock", "<blockquote>")}><Quotes size={16} /></Button>
                <Button type="button" variant="ghost" size="icon" title="Insert link"
                        onMouseDown={stopStealFocus}
                        onClick={() => {
                          const u = window.prompt("Link URL");
                          if (u) exec("createLink", u);
                        }}><LinkSimple size={16} /></Button>
              </div>
              <div
                ref={bodyRef}
                contentEditable
                data-testid="post-body-editor"
                className="min-h-[200px] max-h-[400px] overflow-y-auto p-3 text-sm outline-none focus:outline-none prose prose-sm max-w-none"
                suppressContentEditableWarning
              />
            </div>
          </div>

          {/* Topics */}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Topics (up to 10)</Label>
            <div className="flex flex-wrap items-center gap-1.5">
              {topics.map(t => (
                <Badge key={t} variant="outline" className="gap-1 pl-2 pr-1">
                  #{t}
                  <button type="button" onClick={() => removeTopic(t)}
                          className="hover:bg-muted rounded px-0.5"
                          data-testid={`topic-remove-${t}`}>
                    <X size={12} />
                  </button>
                </Badge>
              ))}
              <Input
                value={topicsInput}
                onChange={e => setTopicsInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addTopic(topicsInput);
                    setTopicsInput("");
                  }
                }}
                onBlur={() => { if (topicsInput.trim()) { addTopic(topicsInput); setTopicsInput(""); } }}
                placeholder="Type a topic and press Enter"
                className="flex-1 min-w-[160px] h-8 text-xs"
                data-testid="post-topic-input"
              />
            </div>
            <div className="flex flex-wrap gap-1 pt-1">
              {topicSuggestions.filter(t => !topics.includes(t)).slice(0, 6).map(t => (
                <button
                  key={t} type="button"
                  onClick={() => addTopic(t)}
                  className="text-[11px] text-sky-700 hover:text-sky-900 underline"
                >
                  +#{t}
                </button>
              ))}
            </div>
          </div>

          {/* Media */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Media</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="border border-dashed border-input rounded-md p-3 flex flex-col items-center justify-center text-center">
                <ImageIcon size={20} weight="duotone" className="text-muted-foreground" />
                <span className="text-[11px] text-muted-foreground mt-1">Add image</span>
                <FileUpload
                  variant="avatar" size={40} value={null}
                  onChange={addImage}
                  entityType="publish_post"
                  accept="image/jpeg,image/png,image/webp"
                />
              </div>
              <div className="border border-dashed border-input rounded-md p-3 flex flex-col items-center justify-center text-center">
                <FilePdf size={20} weight="duotone" className="text-muted-foreground" />
                <span className="text-[11px] text-muted-foreground mt-1">Add PDF</span>
                <FileUpload
                  variant="avatar" size={40} value={null}
                  onChange={addPdf}
                  entityType="publish_post"
                  accept="application/pdf"
                />
              </div>
              <div className="border border-dashed border-input rounded-md p-3 space-y-1">
                <div className="flex items-center justify-center gap-1 text-muted-foreground">
                  <YoutubeLogo size={20} weight="duotone" />
                  <span className="text-[11px]">Embed YouTube</span>
                </div>
                <div className="flex gap-1">
                  <Input value={youtubeUrl} onChange={e => setYoutubeUrl(e.target.value)}
                         placeholder="https://youtube.com/…"
                         className="h-8 text-xs"
                         data-testid="post-youtube-input" />
                  <Button size="icon" variant="outline" onClick={addYouTube}
                          data-testid="post-youtube-add"><Plus size={14} /></Button>
                </div>
              </div>
            </div>
            {media.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {media.map((m, i) => {
                  // Prefer stable identifiers over the array index so React
                  // can preserve DOM nodes when the user removes an item.
                  const stableKey = m.file_id || m.url || `${m.kind}-${i}`;
                  return (
                  <div key={stableKey}
                       className="border border-border rounded-md p-2 flex items-center gap-2 text-xs"
                       data-testid={`post-media-${i}`}>
                    {m.kind === "youtube" && <YoutubeLogo size={16} className="text-rose-600" />}
                    {m.kind === "pdf" && <FilePdf size={16} className="text-sky-600" />}
                    {m.kind === "image" && <ImageIcon size={16} className="text-emerald-600" />}
                    <span className="flex-1 truncate">{m.url || m.file_id || m.kind}</span>
                    <button onClick={() => removeMedia(i)} className="text-muted-foreground hover:text-rose-600">
                      <X size={14} />
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* CTA (ads only) */}
          {postType === "advertisement" && (
            <div className="border border-rose-200 bg-rose-50 rounded-md p-3 space-y-2">
              <Label className="text-xs uppercase tracking-wider text-rose-700">Call to action</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input value={ctaLabel} onChange={e => setCtaLabel(e.target.value)}
                       placeholder="Button label (e.g. Register)"
                       data-testid="post-cta-label" />
                <Input value={ctaUrl} onChange={e => setCtaUrl(e.target.value)}
                       placeholder="https://…"
                       data-testid="post-cta-url" />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}
                  data-testid="post-cancel-btn">Cancel</Button>
          {(!loaded || loaded.status === "draft") && (
            <Button variant="outline" onClick={() => submit(false)} disabled={saving}
                    data-testid="post-save-draft-btn">
              Save draft
            </Button>
          )}
          <Button onClick={() => submit(true)} disabled={saving}
                  data-testid="post-publish-btn">
            {saving ? "Saving…" : (
              loaded?.status === "published" ? "Update & Republish" :
              loaded?.status === "delinked" ? "Republish" :
              loaded?.status === "draft" ? "Publish" :
              "Publish"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
